import {
  Box,
  Button,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material"
import { FC, useEffect, useRef, useState } from "react"
import { store } from "@core/store"
import { ContentAsset } from "../../interfaces"
import { useAssetLink, useAssetToken } from "../../hooks/useAssetLink"
import { getAssetLink, removeContent } from "../../services/assetLinkCache"
import {
  assetLinkErrorMessage,
  normalizeContentError,
} from "../../utils/contentErrors"
import { contentsApi } from "../../store/contentsApi"

interface IProps {
  contentId: number
  asset: ContentAsset
}

const previewMediaSx = {
  maxWidth: "min(100%, 320px)",
  maxHeight: 320,
  width: "auto",
  height: "auto",
  objectFit: "contain",
  alignSelf: "flex-start",
} as const

const AssetPreviewSession: FC<IProps> = ({ contentId, asset }) => {
  const { state, onMediaError, retry } = useAssetLink({
    contentId,
    assetId: asset.id,
  })
  const token = useAssetToken()
  const sessionRef = useRef<{
    token: typeof token
    controller: AbortController | null
  } | null>(null)
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const [showFallback, setShowFallback] = useState(false)

  useEffect(() => {
    const session = { token, controller: null as AbortController | null }
    sessionRef.current = session
    setDownloading(false)
    setDownloadError(null)
    setShowFallback(false)
    return () => {
      session.controller?.abort()
      sessionRef.current = null
    }
  }, [token])

  const download = async (currentTab = false) => {
    const session = sessionRef.current
    if (
      !session ||
      session.controller ||
      session.token !== store.getState().auth.token
    )
      return
    const controller = new AbortController()
    session.controller = controller
    const isCurrent = () =>
      sessionRef.current === session &&
      !controller.signal.aborted &&
      session.token === store.getState().auth.token
    setDownloading(true)
    setDownloadError(null)
    setShowFallback(false)
    try {
      // Перепроверяем TTL на клик; файл браузер получает напрямую из MinIO.
      const link = await getAssetLink(contentId, asset.id)
      if (!isCurrent()) return
      if (currentTab) {
        // Явный запасной путь не требует разрешения на всплывающие окна.
        const anchor = document.createElement("a")
        anchor.href = link.url
        anchor.target = "_self"
        anchor.rel = "noopener noreferrer"
        anchor.referrerPolicy = "no-referrer"
        document.body.appendChild(anchor)
        try {
          anchor.click()
        } finally {
          anchor.remove()
        }
      } else if (!window.open(link.url, "_blank", "noopener,noreferrer")) {
        setShowFallback(true)
      }
    } catch (error: unknown) {
      if (!isCurrent()) return
      const { status, code } = normalizeContentError(error)
      if (
        status === 404 &&
        (code === "asset_not_found" || code === "content_not_found")
      ) {
        removeContent(contentId)
        store.dispatch(
          contentsApi.util.invalidateTags([
            { type: "Contents", id: contentId },
          ]),
        )
      }
      setDownloadError(assetLinkErrorMessage(error))
    } finally {
      if (isCurrent()) {
        session.controller = null
        setDownloading(false)
      }
    }
  }

  const missing = state.status === "error" && state.reason !== "load_error"

  return (
    <Stack spacing={1}>
      <Typography variant="body2">{asset.filename}</Typography>
      {state.status === "loading" && (
        <CircularProgress size={24} aria-label="Загрузка файла" />
      )}
      {state.status === "ready" &&
        (asset.media_type === "video" ? (
          <Box
            component="video"
            controls
            preload="metadata"
            src={state.url}
            onError={onMediaError}
            aria-label={asset.filename}
            sx={previewMediaSx}
          />
        ) : (
          <Box
            component="img"
            src={state.url}
            alt={asset.filename}
            loading="lazy"
            onError={onMediaError}
            sx={previewMediaSx}
          />
        ))}
      {state.status === "error" && (
        <>
          <Typography role="alert" color="error" variant="body2">
            {state.message}
          </Typography>
          {state.reason === "load_error" && (
            <Button onClick={retry}>Повторить</Button>
          )}
        </>
      )}
      <Button
        onClick={() => {
          void download()
        }}
        disabled={downloading || missing}
      >
        Скачать файл
      </Button>
      {downloadError && (
        <Typography role="alert" color="error" variant="body2">
          {downloadError}
        </Typography>
      )}
      {showFallback && (
        <Link
          component="button"
          type="button"
          disabled={downloading || missing}
          onClick={() => {
            void download(true)
          }}
        >
          Открыть файл
        </Link>
      )}
    </Stack>
  )
}

// Смена пары ID завершает весь старый цикл, включая ожидающее скачивание.
// Родитель просмотрщика монтирует превью только пока диалог открыт.
export const AssetPreview: FC<IProps> = (props) => (
  <AssetPreviewSession
    key={`${props.contentId}:${props.asset.id}:${props.asset.media_type}`}
    {...props}
  />
)
